import type { AxiosInstance, InternalAxiosRequestConfig } from "axios";
import { isCanceledError } from "../utils/cancellation.util";

const shouldSkipGlobalLoading = (
  config?: InternalAxiosRequestConfig,
): boolean => {
  if (!config) {
    return false;
  }

  return config.skipGlobalLoading === true;
};

// Guards against re-registering interceptors (e.g. React StrictMode double-invoking
// effects), which would call onStart/onStop multiple times per request and, combined
// with the never-resolving cancellation swallow below, permanently unbalance the count.
const instancesWithLoader = new WeakSet<AxiosInstance>();

const setupAxiosLoader = (
  axiosInstance: AxiosInstance,
  onStart: () => void,
  onStop: () => void,
): void => {
  if (instancesWithLoader.has(axiosInstance)) {
    return;
  }
  instancesWithLoader.add(axiosInstance);

  // REQUEST
  axiosInstance.interceptors.request.use(
    (config: InternalAxiosRequestConfig) => {
      if (!shouldSkipGlobalLoading(config)) {
        onStart();
      }
      return config;
    },
    (error) => {
      if (!shouldSkipGlobalLoading(error?.config)) {
        onStop();
      }
      return Promise.reject(error);
    },
  );

  // RESPONSE
  axiosInstance.interceptors.response.use(
    (response) => {
      if (!shouldSkipGlobalLoading(response.config)) {
        onStop();
      }
      return response;
    },
    (error) => {
      if (!shouldSkipGlobalLoading(error?.config)) {
        onStop();
      }

      if (isCanceledError(error)) {
        // Superseded by a newer identical request; never resolve so no error dialog appears.
        return new Promise(() => {
          /* never settle: superseded request, no error dialog */
        });
      }

      const string =
        typeof error === "string"
          ? error
          : error?.response?.data?.message ||
            error.message ||
            error.errorMessages?.join(", ") ||
            "An error occurred.";
      return Promise.reject(string);
    },
  );
};

export default setupAxiosLoader;
